import React, { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Upload, X, ArrowRight, Phone, Mail, MessageCircle } from "lucide-react";
import { CONTACT } from "@/lib/contact";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_PHOTOS = 6;
const MAX_SIZE = 5 * 1024 * 1024;

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function QuoteForm() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", postcode: "", message: "" });
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileRef = useRef(null);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = MAX_PHOTOS - photos.length;
    if (room <= 0) {
      toast.error(`You can attach up to ${MAX_PHOTOS} photos.`);
      return;
    }
    const accepted = [];
    for (const f of files.slice(0, room)) {
      if (!f.type.startsWith("image/")) { toast.error(`${f.name} is not an image.`); continue; }
      if (f.size > MAX_SIZE) { toast.error(`${f.name} is over 5MB.`); continue; }
      try {
        const dataUrl = await readFileAsDataURL(f);
        accepted.push({ filename: f.name, content_type: f.type, data: dataUrl, preview: dataUrl });
      } catch { toast.error(`Could not read ${f.name}`); }
    }
    setPhotos((p) => [...p, ...accepted]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removePhoto = (idx) => setPhotos((p) => p.filter((_, i) => i !== idx));

  const validate = () => {
    if (!form.name.trim()) return "Please enter your name.";
    if (!form.phone.trim() || form.phone.trim().length < 6) return "Please enter a valid phone number.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Please enter a valid email.";
    if (!form.postcode.trim()) return "Please enter your postcode.";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        postcode: form.postcode.trim(),
        message: form.message.trim(),
        photos: photos.map((p) => ({ filename: p.filename, content_type: p.content_type, data: p.data })),
        service_interest: "Construction & Refurbishment",
        source_page: "home",
      };
      const res = await axios.post(`${API}/quotes`, payload, { headers: { "Content-Type": "application/json" } });
      if (res.status === 201 || res.status === 200) {
        toast.success("Quote request received. We'll be in touch today.");
        setSubmitted(true);
        setForm({ name: "", phone: "", email: "", postcode: "", message: "" });
        setPhotos([]);
      } else {
        toast.error("Something went wrong. Please try calling us.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "We couldn't submit the form. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid="quote-form-section"
      className="bg-[#F5EFE5] text-[#2B2420] py-20 md:py-28 border-y border-[#E5DDD0]"
    >
      <div className="px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        {/* Left copy */}
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-[#B8732C] mb-5">
            <span className="brand-gold-divider" /> Request a Free Quote
          </div>
          <h2
            className="font-black tracking-tighter leading-[0.95] text-4xl md:text-5xl lg:text-6xl text-[#2B2420]"
            style={{ fontFamily: "Cabinet Grotesk" }}
          >
            Send photos. <br /> Get a quick quote <br />
            <span className="text-[#B8732C]">today.</span>
          </h2>
          <p className="mt-6 text-[#4D4037] text-base md:text-lg leading-relaxed max-w-md">
            Drop a few photos, your postcode and a short note. Most estimates
            returned within 2 working hours — completely free, no obligation.
          </p>

          <div className="mt-8 space-y-3 text-sm">
            <a
              href={`tel:${CONTACT.primary.tel}`}
              className="flex items-center gap-3 text-[#2B2420] hover:text-[#B8732C]"
              data-testid="form-side-phone-primary"
            >
              <span className="w-10 h-10 border border-[#2B2420]/15 flex items-center justify-center bg-white">
                <Phone className="w-4 h-4 text-[#B8732C]" />
              </span>
              <span>
                <span className="block font-bold">{CONTACT.primary.display}</span>
                <span className="text-[#8A7F74] text-xs">{CONTACT.primary.name}</span>
              </span>
            </a>
            <a
              href={`tel:${CONTACT.secondary.tel}`}
              className="flex items-center gap-3 text-[#2B2420] hover:text-[#B8732C]"
              data-testid="form-side-phone-secondary"
            >
              <span className="w-10 h-10 border border-[#2B2420]/15 flex items-center justify-center bg-white">
                <Phone className="w-4 h-4 text-[#B8732C]" />
              </span>
              <span>
                <span className="block font-bold">{CONTACT.secondary.display}</span>
                <span className="text-[#8A7F74] text-xs">{CONTACT.secondary.name}</span>
              </span>
            </a>
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 text-[#2B2420] hover:text-[#B8732C]"
              data-testid="form-side-whatsapp"
            >
              <span className="w-10 h-10 border border-[#2B2420]/15 flex items-center justify-center bg-white">
                <MessageCircle className="w-4 h-4 text-[#B8732C]" />
              </span>
              WhatsApp us anytime
            </a>
            <a
              href={`mailto:${CONTACT.email}`}
              className="flex items-center gap-3 text-[#2B2420] hover:text-[#B8732C]"
              data-testid="form-side-email"
            >
              <span className="w-10 h-10 border border-[#2B2420]/15 flex items-center justify-center bg-white">
                <Mail className="w-4 h-4 text-[#B8732C]" />
              </span>
              <span className="break-all">{CONTACT.email}</span>
            </a>
          </div>
        </div>

        {/* Form card */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white p-7 md:p-10 border border-[#E5DDD0] shadow-[0_24px_48px_-28px_rgba(15,23,42,0.18)]"
          >
            {submitted ? (
              <div data-testid="quote-form-success" className="py-8 text-center">
                <div className="text-xs uppercase tracking-[0.3em] text-[#C9A24C] mb-4 font-bold">Request received</div>
                <h3 className="font-black text-3xl md:text-4xl tracking-tight" style={{ fontFamily: "Cabinet Grotesk" }}>
                  Thanks — we're on it.
                </h3>
                <p className="mt-4 text-[#8A7F74] max-w-md mx-auto">
                  A member of the Constantinov team will be in touch today with your estimate. For anything urgent, call{" "}
                  <a href={`tel:${CONTACT.primary.tel}`} className="underline text-[#2B2420]">{CONTACT.primary.display}</a>.
                </p>
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  data-testid="quote-form-reset"
                  className="mt-8 inline-flex items-center gap-2 border border-[#2B2420] hover:bg-[#2B2420] hover:text-white px-5 py-3 text-xs font-bold uppercase tracking-[0.15em] transition-colors"
                >
                  Submit another request
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="quote-form" className="space-y-5">
                <div className="flex items-center justify-between pb-2">
                  <h3 className="font-black text-2xl md:text-3xl tracking-tight" style={{ fontFamily: "Cabinet Grotesk" }}>
                    Free Quote · 2 min form
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Full name" name="name" value={form.name} onChange={handleChange} placeholder="Jane Smith" testid="form-input-name" required />
                  <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="07123 456789" testid="form-input-phone" required />
                  <Field label="Email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="you@example.co.uk" testid="form-input-email" required />
                  <Field label="Postcode" name="postcode" value={form.postcode} onChange={handleChange} placeholder="E14 5AB" testid="form-input-postcode" required />
                </div>

                <div>
                  <label className="block text-[11px] uppercase tracking-[0.25em] text-[#8A7F74] mb-2 font-bold">Message</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Tell us about your project — rooms, scope, timescale, access…"
                    data-testid="form-input-message"
                    className="w-full px-4 py-3 bg-[#F5EFE5] border border-[#E5DDD0] focus:border-[#B8732C] focus:outline-none text-[#2B2420] placeholder:text-[#A89B8F] text-sm"
                  />
                </div>

                <div>
                  <label className="block text-[11px] uppercase tracking-[0.25em] text-[#8A7F74] mb-2 font-bold">
                    Upload Photos · up to {MAX_PHOTOS}
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {photos.map((p, i) => (
                      <div
                        key={i}
                        data-testid={`photo-preview-${i}`}
                        className="relative w-20 h-20 md:w-24 md:h-24 border border-[#E5DDD0] overflow-hidden bg-[#F5EFE5]"
                      >
                        <img src={p.preview} alt={p.filename} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removePhoto(i)}
                          data-testid={`photo-remove-${i}`}
                          className="absolute top-1 right-1 bg-[#2B2420] text-white w-6 h-6 flex items-center justify-center hover:bg-[#B8732C]"
                          aria-label="Remove photo"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                    {photos.length < MAX_PHOTOS && (
                      <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        data-testid="photo-upload-btn"
                        className="w-20 h-20 md:w-24 md:h-24 border border-dashed border-[#2B2420]/30 hover:border-[#B8732C] hover:text-[#B8732C] text-[#8A7F74] flex flex-col items-center justify-center gap-1 transition-colors bg-white"
                      >
                        <Upload className="w-5 h-5" />
                        <span className="text-[10px] font-bold uppercase tracking-wider">Add</span>
                      </button>
                    )}
                  </div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={onFilesSelected}
                    className="hidden"
                    data-testid="photo-file-input"
                  />
                  <p className="mt-2 text-[11px] text-[#8A7F74]">JPG / PNG / HEIC · max 5MB each.</p>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="quote-form-submit"
                  className="w-full bg-[#B8732C] hover:bg-[#9A5D1E] disabled:opacity-60 disabled:cursor-not-allowed text-white py-5 text-sm font-bold uppercase tracking-[0.15em] flex items-center justify-center gap-3 transition-colors"
                >
                  {submitting ? "Sending…" : "Send Photos for Quick Estimate"}
                  {!submitting && <ArrowRight className="w-4 h-4" />}
                </button>

                <p className="text-[11px] text-[#8A7F74] text-center">
                  By submitting you agree to be contacted about your quote. We don't share your details.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, placeholder, type = "text", testid, required }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.25em] text-[#8A7F74] mb-2 font-bold">
        {label}{required ? " *" : ""}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
        required={required}
        className="w-full px-4 py-3 bg-[#F5EFE5] border border-[#E5DDD0] focus:border-[#B8732C] focus:outline-none text-[#2B2420] placeholder:text-[#A89B8F] text-sm"
      />
    </div>
  );
}
